import React, { useContext } from "react";
import { FilterCtx } from "./SearchList";
import Grid from "@material-ui/core/Grid";
import Check from "@material-ui/icons/Check";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import allStyles from "assets/jss/elite-feats-cms/views/extendedTablesStyle";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { useTranslation } from "react-i18next";
import ClientSelectionCard, { ClientSummary } from "./ClientSelectionCard";

const styles = {
  ...allStyles,
  table: {
    maxWidth: 600,
    minHeight: 100,
  },
};

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    maxWidth: 275,
    whiteSpace: "normal",
    wordWrap: "break-word",
  },
  body: {
    fontSize: 14,
    maxWidth: 275,
    whiteSpace: "normal",
    wordWrap: "break-word",
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const useStyles = makeStyles(styles);

export interface ClientItem {
  email: string;
  first_name?: string;
  last_name?: string;
  [key: string]: unknown;
}

interface ClientsProps {
  checked: Record<string, boolean>;
  data: ClientItem[];
  handleToggle: (event: { target: { name?: string; checked: boolean } }) => void;
  reload?: unknown;
  isMobile?: boolean;
}

function Clients(props: ClientsProps): React.ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();
  const [filter, setFilter] = useContext(FilterCtx);

  // Filter and sort clients (used by both card and table views)
  function getFilteredClients(clients: ClientItem[]): ClientItem[] {
    let filtered = clients.filter(
      (client: ClientItem) =>
        client.first_name!.toLowerCase().includes(filter.toLowerCase()) ||
        client.last_name!.toLowerCase().includes(filter.toLowerCase()) ||
        client.email.toLowerCase().includes(filter.toLowerCase())
    );

    let front: ClientItem[] = [];
    let back: ClientItem[] = [];
    //sorts all clients to bring group members to top of the list
    filtered.forEach((client: ClientItem) => {
      if (props.checked[client.email]) {
        front.push(client);
      } else {
        back.push(client);
      }
    });
    return front.concat(back);
  }

  function gen_list(clients: ClientItem[]) {
    let processed = getFilteredClients(clients);

    return processed.map((client: ClientItem, i: number) => {
      return {
        checkBox: (
          <Checkbox
            key={Math.random()}
            style={{ padding: 5 }}
            name={`${client.email}`}
            onChange={props.handleToggle}
            checked={props.checked[`${client.email}`]}
            checkedIcon={<Check className={(classes as Record<string, string>).checkedIcon} />}
            icon={<Check className={(classes as Record<string, string>).uncheckedIcon} />}
            classes={{
              checked: (classes as Record<string, string>).checkedBlue,
              root: (classes as Record<string, string>).checkBlue,
            }}
          />
        ),
        name: (
          <Typography style={{ fontSize: 14 }}>
            {`${client.first_name} ${client.last_name}`}{" "}
          </Typography>
        ),
        email: (
          <Typography style={{ fontSize: 14 }}>{`${client.email}`} </Typography>
        ),
      };
    });
  }

  // Handle checkbox toggle for cards
  const handleCardToggle = (client: ClientSummary) => {
    const event = {
      target: {
        name: client.email,
        checked: !props.checked[client.email],
      },
    };
    props.handleToggle(event);
  };

  // Mobile card view
  if (props.isMobile) {
    const filteredClients = getFilteredClients(props.data);

    return (
      <div style={{ marginTop: 10 }}>
        {filteredClients.map((client, index) => (
          <ClientSelectionCard
            key={client.email}
            client={{
              name: `${client.first_name} ${client.last_name}`,
              email: client.email,
            }}
            isChecked={props.checked[client.email] || false}
            onChange={handleCardToggle}
          />
        ))}
      </div>
    );
  }

  // Desktop table view
  const filteredClients = getFilteredClients(props.data);
  const currentMembers = filteredClients.filter(client => props.checked[client.email]);
  const availableClients = filteredClients.filter(client => !props.checked[client.email]);

  return (
    <div>
      {currentMembers.length > 0 && (
        <Typography
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#40628d",
            marginTop: 10,
            marginBottom: 5,
            paddingLeft: 10,
          }}
        >
          {t("groups:currentMembers")} ({currentMembers.length})
        </Typography>
      )}

      <TableContainer
        className="search-list"
        component={Paper}
        style={{ marginTop: 10 }}
        variant="outlined"
      >
        <Table className={classes.table} size="small" aria-label="simple table">
          <TableHead>
            <TableRow>
              <StyledTableCell style={{ width: 20 }}></StyledTableCell>
              <StyledTableCell align="center">
                {t("clients:name")}
              </StyledTableCell>
              <StyledTableCell align="center">
                {t("clients:email")}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Current Members - with background color */}
            {gen_list(currentMembers).map((row, index) => (
              <StyledTableRow
                key={`member-${Math.random()}`}
                className={`search-row-${index}`}
                style={{ backgroundColor: '#e7f6ff' }}
              >
                <StyledTableCell
                  align="center"
                  style={{
                    borderRight: "1px solid #dddddd",
                    paddingLeft: 10,
                    paddingRight: 10,
                  }}
                >
                  <div className={`search-checkbox-${index}`}>{row.checkBox}</div>
                </StyledTableCell>
                <StyledTableCell
                  align="left"
                  style={{
                    borderRight: "1px solid #dddddd",
                  }}
                >
                  {row.name}
                </StyledTableCell>
                <StyledTableCell align="left">{row.email}</StyledTableCell>
              </StyledTableRow>
            ))}

            {/* Divider row if both sections exist */}
            {currentMembers.length > 0 && availableClients.length > 0 && (
              <TableRow>
                <StyledTableCell colSpan={3} style={{ padding: 0, backgroundColor: '#fff' }}>
                  <Divider />
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#999",
                      margin: "8px 0 8px 10px",
                    }}
                  >
                    {t("groups:availableClients")} ({availableClients.length})
                  </Typography>
                </StyledTableCell>
              </TableRow>
            )}

            {/* Available Clients - normal background */}
            {gen_list(availableClients).map((row, index) => (
              <StyledTableRow
                key={`available-${Math.random()}`}
                className={`search-row-${currentMembers.length + index}`}
              >
                <StyledTableCell
                  align="center"
                  style={{
                    borderRight: "1px solid #dddddd",
                    paddingLeft: 10,
                    paddingRight: 10,
                  }}
                >
                  <div className={`search-checkbox-${currentMembers.length + index}`}>{row.checkBox}</div>
                </StyledTableCell>
                <StyledTableCell
                  align="left"
                  style={{
                    borderRight: "1px solid #dddddd",
                  }}
                >
                  {row.name}
                </StyledTableCell>
                <StyledTableCell align="left">{row.email}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default Clients;
